import { z } from 'zod';
/**
 * Typed Zod schemas for Sharefox API tools surfaced in V1 draft responses.
 *
 * Derived from openapi_en.json (https://api.mysharefox.com/docs).
 * Catalogue and pricing in the live backend use getProducts, getProduct, and
 * getProductAvailability — legacy getCatalog / getPricing were removed.
 */
// ── Tool: getProductAvailability ───────────────────────────────────────────
// Maps to: GET /product-availability/{productId}
// Required headers in production: x-sharefox-shop-domain
// Required query params: startDate, endDate, startTimeId, endTimeId
export const GetProductAvailabilityInputSchema = z.object({
    productId: z.number().describe('Sharefox product ID from the catalogue'),
    shopDomain: z.string().describe('Merchant shop domain, e.g. nordicrentalco.mysharefox.com'),
    startDate: z.string().describe('ISO 8601 date, e.g. 2026-05-10'),
    endDate: z.string().describe('ISO 8601 date, e.g. 2026-05-12'),
    startTimeId: z.number().optional().describe('Handover time slot ID (default: first available)'),
    endTimeId: z.number().optional().describe('Return time slot ID (default: first available)'),
});
// Response projection from Sharefox GET /product-availability response.
export const ProductAvailabilityResultSchema = z.object({
    productId: z.number(),
    productName: z.string(),
    available: z.boolean().describe('True if availableUnits > 0 for the requested date range'),
    availableUnits: z.number().describe('Sharefox productAvailability field'),
    pricePerDay: z
        .number()
        .describe('Price per day incl. VAT in the shop currency (Sharefox: product.price)'),
    pricePerDayExclVat: z.number(),
    priceFinal: z.number().describe('Total price for the full rental period incl. VAT'),
    depositAmount: z.number(),
    currency: z.string().describe('ISO 4217 currency code from the shop'),
    startDate: z.string(),
    endDate: z.string(),
    rentalDays: z.number(),
});
// ── Tool: getOrder ─────────────────────────────────────────────────────────
// Maps to: GET /orders/{bookingReference}
export const GetOrderInputSchema = z.object({
    bookingReference: z
        .string()
        .describe('Booking reference from customer email, e.g. SF-12031 or NO39'),
    shopDomain: z.string(),
});
export const OrderProductSchema = z.object({
    name: z.string(),
    price: z.number(),
    units: z.number(),
    pricePerDay: z.number().optional(),
});
export const OrderResultSchema = z.object({
    orderId: z.number(),
    bookingReference: z.string(),
    status: z.string().describe('Sharefox order status, e.g. "confirmed", "active", "returned"'),
    pickUp: z.string().describe('Formatted pickup datetime string'),
    dropOff: z.string().describe('Formatted drop-off datetime string'),
    customerName: z.string(),
    customerEmail: z.string(),
    products: z.array(OrderProductSchema),
    totalPrice: z.number().optional(),
    currency: z.string().optional().describe('ISO 4217 currency code from the shop'),
});
// ── Tool: putOrderNote ────────────────────────────────────────────────────
export const PutOrderNoteInputSchema = z.object({
    orderId: z.number().describe('Numeric Sharefox order ID (from getOrder result)'),
    note: z.string().max(500).describe('Delivery instruction or internal note from the customer'),
    adminDomain: z.string().describe('Admin domain, e.g. admin.nordicrentalco.mysharefox.com'),
});
export const OrderNoteResultSchema = z.object({
    success: z.boolean(),
    orderId: z.number(),
    note: z.string(),
});
// ── ToolResult union ───────────────────────────────────────────────────────
export const ToolResultSchema = z.discriminatedUnion('tool', [
    z.object({
        tool: z.literal('getProductAvailability'),
        input: GetProductAvailabilityInputSchema,
        result: ProductAvailabilityResultSchema,
    }),
    z.object({
        tool: z.literal('getOrder'),
        input: GetOrderInputSchema,
        result: OrderResultSchema,
    }),
    z.object({
        tool: z.literal('putOrderNote'),
        input: PutOrderNoteInputSchema,
        result: OrderNoteResultSchema,
    }),
]);
export const SHAREFOX_TOOL_NAMES = ['getProductAvailability', 'getOrder', 'putOrderNote'];
export const TOOL_DISPLAY_NAMES = {
    getProductAvailability: 'Sharefox Availability',
    getOrder: 'Sharefox Order Lookup',
    putOrderNote: 'Sharefox Order Note',
};
//# sourceMappingURL=sharefox.js.map